import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';
import { faEllipsis } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { GlobalDropdown } from '@components/basics/Dropdowns/GlobalDropdown';
import { ExportCSVMenuItem } from '@components/basics/Dropdowns/MenuItems/ExportCSVMenuItem';
import { Chip } from '@components/main-components/elements/chips/Chip';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { useToast } from '@context/toast';
import type { FragmentType } from '@graphql/reactQuery';
import {
  AudienceSongInfoFragmentDoc,
  EmptyAudienceFragmentDoc,
  TierNft,
} from '@graphql/reactQuery';
import { fetchQuery, getFragment, ReleaseBackersCsvUrlDocument } from '@graphql/reactQuery';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { SectionTitle } from '../shared';
import { EmptyAudience } from './Empty';
import { HighlightedAudience } from './Highlighted';
import { AudienceSeats } from './Seats';
import type { AudienceSortType } from './constants';

export function EditionAudience({
  releaseFrag,
  isFullPage,
  tabsTopOffset,
}: {
  releaseFrag: FragmentType<AudienceSongInfoFragmentDoc>;
  isFullPage: boolean;
  tabsTopOffset: number | undefined;
}) {
  const [sort, setSort] = useState<AudienceSortType>('top');
  const release = getFragment(AudienceSongInfoFragmentDoc, releaseFrag);

  const {
    artist: { id: artistId, userId },
    mintStartTimestamp,
  } = getFragment(EmptyAudienceFragmentDoc, release);

  const hasSaleStarted = mintStartTimestamp <= Date.now();

  const { openToast } = useToast();

  const { data: subscriptionData } = useSubscribeRelease({
    release,
  });

  const hasAudience = subscriptionData.totalMinted > 0;

  const disabled = !hasAudience;

  const downloadCSV = async () => {
    try {
      const {
        data: { releaseBackersCSVUrl },
      } = await fetchQuery(ReleaseBackersCsvUrlDocument, {
        variables: {
          releaseId: release.id,
        },
      });
      if (releaseBackersCSVUrl) {
        window.open(releaseBackersCSVUrl);
      } else {
        throw Error('Could not find release backers CSV file');
      }
    } catch (e) {
      // TODO: linear.app/sound/issue/ENG-9185/add-missing-observability-in-fe
      openToast({
        text: 'Download CSV release backers failed.',
        variant: 'error',
      });
    }
  };

  return (
    <div className={twMerge('flex h-full w-full flex-col gap-6', !isFullPage && 'mt-8 md:mt-16')}>
      <div className="w-full">
        {!isFullPage && (
          <div className="flex w-full items-center justify-between">
            <SectionTitle title="Audience" />

            {/* For now do not show export CSV on mobile devices */}
            <div className="hidden md:block">
              <GlobalDropdown
                align="end"
                css={{ width: 'auto' }}
                trigger={<IconButton icon={<FontAwesomeIcon icon={faEllipsis} />} />}
                disabled={disabled}
              >
                <ExportCSVMenuItem onClick={downloadCSV} disabled={disabled} />
              </GlobalDropdown>
            </div>
          </div>
        )}

        <HighlightedAudience hasAudience={hasAudience} releaseFrag={release} />

        <div className="flex items-center gap-2">
          <Chip
            text="Top"
            isSelected={sort === 'top'}
            onClick={() => setSort('top')}
            disabled={disabled}
          />
          <Chip
            text="Earliest"
            isSelected={sort === 'earliest'}
            onClick={() => setSort('earliest')}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="relative flex flex-col overflow-visible">
        {hasAudience ? (
          <AudienceSeats
            releaseFrag={release}
            tier={TierNft.All}
            sort={sort}
            paginate={isFullPage}
            tabsTopOffset={tabsTopOffset}
          />
        ) : (
          <EmptyAudience artistId={artistId} hasSaleStarted={hasSaleStarted} userId={userId} />
        )}
      </div>
    </div>
  );
}
